/**
 * Sanificazione HTML a elenco di consentiti, basata sul DOM (nessuna dipendenza).
 *
 * L'HTML viene analizzato con DOMParser (documento inerte: niente script
 * eseguiti, niente risorse caricate) e ricostruito tenendo SOLO i tag e gli
 * attributi ammessi. Pensata per contenuti prodotti da un editor ricco (es. la
 * nota delle circolari) e resi con dangerouslySetInnerHTML.
 *
 * Le regole sono speculari al filtro del server (backend/utils/htmlSanitize.js,
 * sanificaHtmlRicco): se cambiano qui, vanno cambiate anche lì.
 */

const HTML_NS = 'http://www.w3.org/1999/xhtml';

const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'blockquote', 'pre', 'code', 'ul', 'ol', 'li',
  'a', 'img', 'span', 'div',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'figure', 'figcaption',
  'mark', 'small', 'del', 'ins', 'strike', 'dl', 'dt', 'dd',
  'abbr', 'cite', 'q', 'kbd', 'samp', 'var', 'address',
]);

// Elementi di blocco non ammessi: diventano un <div> con i figli ripuliti, così
// il testo di due blocchi vicini non si incolla.
const BLOCK_TO_DIV = new Set([
  'section', 'article', 'header', 'footer', 'aside', 'nav', 'main',
  'details', 'summary', 'fieldset', 'legend', 'center',
]);

// Eliminati con tutto il contenuto (gli altri tag non consentiti vengono
// sostituiti dai loro figli ripuliti).
const DROP_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'svg', 'math',
  'link', 'meta', 'base', 'template', 'noscript', 'xmp', 'textarea', 'noembed', 'noframes',
  'plaintext', 'title', 'head', 'select', 'option', 'button', 'input', 'frame', 'frameset',
]);

// Attributi ammessi su qualunque tag consentito.
const GLOBAL_ATTRS = new Set(['title', 'class', 'style', 'width', 'height']);

const TABLE_TAGS = ['table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot', 'col', 'colgroup'];
const TABLE_ATTRS = ['border', 'cellpadding', 'cellspacing', 'align', 'valign', 'bgcolor'];
const ALIGN_TAGS = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img'];

// Attributi ammessi solo su alcuni tag.
const TAG_ATTRS: Record<string, Set<string>> = {};
const allowAttrs = (tags: string[], attrs: string[]): void => {
  for (const tag of tags) {
    const set = TAG_ATTRS[tag] || (TAG_ATTRS[tag] = new Set());
    for (const attr of attrs) set.add(attr);
  }
};
allowAttrs(['a'], ['href', 'target', 'rel']);
allowAttrs(['img'], ['src', 'alt']);
allowAttrs(['ol'], ['start', 'type', 'reversed']);
allowAttrs(['th', 'td'], ['colspan', 'rowspan', 'scope', 'headers']);
allowAttrs(['col', 'colgroup'], ['span']);
allowAttrs(['blockquote', 'q'], ['cite']);
allowAttrs(['del', 'ins'], ['datetime']);
allowAttrs(TABLE_TAGS, TABLE_ATTRS);
allowAttrs(ALIGN_TAGS, ['align']);

const isAllowedAttr = (tag: string, name: string): boolean =>
  GLOBAL_ATTRS.has(name) || Boolean(TAG_ATTRS[tag]?.has(name));

// Valori di elenco chiuso.
const ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);
const VALIGN_VALUES = new Set(['top', 'middle', 'bottom', 'baseline']);
const OL_TYPE_VALUES = new Set(['1', 'a', 'A', 'i', 'I']);
const SCOPE_VALUES = new Set(['row', 'col', 'rowgroup', 'colgroup']);
const TARGET_VALUES = new Set(['_blank', '_self']);
const COLOR_VALUE = /^(#[0-9a-f]{3,8}|[a-z]{1,30})$/i;
const HEADERS_VALUE = /^[A-Za-z0-9_:. -]{1,200}$/;
const DATETIME_VALUE = /^[0-9TZ:+. -]{1,40}$/;

// Stesso elenco di PROPRIETA_STILE in backend/utils/htmlSanitize.js (un test in
// ciascun lato lo confronta con la stessa lista attesa).
// Niente font-size, font-family e line-height: il testo esce con il carattere e la
// misura del sito (classe .testo-ricco), non con quelli incollati da Word.
export const ALLOWED_STYLE_PROPS: ReadonlySet<string> = new Set([
  'text-align', 'color', 'background-color', 'font-weight', 'font-style', 'text-decoration',
  'width', 'height', 'vertical-align', 'list-style-type',
  'border-collapse', 'border-spacing', 'border-radius',
  'max-width', 'min-width', 'max-height', 'text-indent', 'float', 'clear', 'white-space',
  ...['margin', 'padding'].flatMap((p) => [p, `${p}-top`, `${p}-right`, `${p}-bottom`, `${p}-left`]),
  ...['', '-top', '-right', '-bottom', '-left'].flatMap((lato) =>
    ['', '-width', '-style', '-color'].map((aspetto) => `border${lato}${aspetto}`)),
]);
const FLOAT_VALUES = new Set(['left', 'right', 'none']);
const BACKSLASH = String.fromCharCode(92);
// «javascript» e «:» sono scritti separati perché la regola no-script-url
// dell'analizzatore segnala la stringa intera anche qui, dove serve proprio a
// riconoscerla e scartarla (con CI=true l'avviso farebbe fallire la build).
// `<`, `>` e `"` restano vietati come sul server (VALORI_STILE_VIETATI).
export const FORBIDDEN_STYLE_VALUES: readonly string[] = ['url(', 'expression', ['javascript', ':'].join(''), '@import', 'image-set(', BACKSLASH, '<', '>', '"'];

const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);
const SAFE_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp)[;,]/i;
// Spazi e caratteri di controllo: i browser li ignorano dentro lo schema
// («java\tscript:»), quindi vanno tolti prima del confronto.
// Intervalli di code point (spazi Unicode e controlli), scritti come numeri per
// non dipendere da caratteri invisibili nel sorgente.
const CONTROL_RANGES: [number, number][] = [
  [0x00, 0x20], [0x7f, 0x9f], [0xa0, 0xa0], [0x1680, 0x1680], [0x2000, 0x200f],
  [0x2028, 0x2029], [0x202f, 0x202f], [0x205f, 0x205f], [0x3000, 0x3000], [0xfeff, 0xfeff],
];
// Nessuno di questi caratteri è speciale dentro una classe [...] della regex.
const CONTROL_AND_SPACE = new RegExp(
  `[${CONTROL_RANGES.map(([a, b]) =>
    a === b ? String.fromCharCode(a) : `${String.fromCharCode(a)}-${String.fromCharCode(b)}`
  ).join('')}]`,
  'g'
);
// Due barre o backslash iniziali in qualunque combinazione: il browser li legge come «//host».
const PROTOCOL_RELATIVE = /^[\\/]{2}/;
const NUMERIC = /^\d{1,5}%?$/;
const INTEGER = /^\d{1,5}$/;

/**
 * URL ammessa (relativa, http, https, mailto, tel; per le immagini anche
 * data:image raster) o null. Un backslash iniziale non è mai ammesso, né «/» o «//»
 * seguiti da backslash (il browser li legge come «//host»); «//host» è ammesso solo
 * se `allowProtocolRelative`, cioè per i link (`href`), non per le immagini.
 */
export function sanitizeUrl(raw: string, isImageSrc = false, allowProtocolRelative = !isImageSrc): string | null {
  const value = (raw || '').trim();
  const compact = value.replace(CONTROL_AND_SPACE, '');
  if (!compact) return null;
  if (compact.startsWith(BACKSLASH) || compact.startsWith(`/${BACKSLASH}`) || compact.startsWith(`//${BACKSLASH}`)) return null;
  if (!allowProtocolRelative && PROTOCOL_RELATIVE.test(compact)) return null;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(compact);
  if (!scheme) return value; // URL relativo
  const name = scheme[1].toLowerCase();
  if (SAFE_SCHEMES.has(name)) return value;
  if (isImageSrc && name === 'data' && SAFE_DATA_IMAGE.test(compact)) return value;
  return null;
}

/** CSS senza commenti; un commento non chiuso toglie tutto il resto (come senzaCommentiCss sul server). */
function withoutCssComments(css: string): string {
  let out = '';
  let from = 0;
  for (;;) {
    const start = css.indexOf('/*', from);
    if (start === -1) return out + css.slice(from);
    out += css.slice(from, start);
    const end = css.indexOf('*/', start + 2);
    if (end === -1) return out;
    from = end + 2;
  }
}

export function sanitizeStyle(raw: string): string {
  const out: string[] = [];
  const noComments = withoutCssComments(raw || '');
  for (const decl of noComments.split(';')) {
    const idx = decl.indexOf(':');
    if (idx <= 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (!value || !ALLOWED_STYLE_PROPS.has(prop)) continue;
    const check = value.toLowerCase().replace(CONTROL_AND_SPACE, '');
    if (FORBIDDEN_STYLE_VALUES.some((v) => check.includes(v))) continue;
    if (prop === 'float' && !FLOAT_VALUES.has(value.toLowerCase())) continue;
    out.push(`${prop}: ${value}`);
  }
  return out.join('; ');
}

/** Valore ripulito dell'attributo (già ammesso sul tag), o null se va scartato. */
function attributeValue(name: string, value: string): string | null {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  switch (name) {
    case 'href':
      return sanitizeUrl(value);
    case 'src':
      return sanitizeUrl(value, true);
    case 'cite':
      return sanitizeUrl(value);
    case 'target':
      return TARGET_VALUES.has(lower) ? lower : null;
    case 'colspan':
    case 'rowspan':
    case 'width':
    case 'height':
    case 'border':
    case 'cellpadding':
    case 'cellspacing':
      return NUMERIC.test(trimmed) ? trimmed : null;
    case 'start':
    case 'span':
      return INTEGER.test(trimmed) ? trimmed : null;
    case 'type':
      return OL_TYPE_VALUES.has(trimmed) ? trimmed : null;
    case 'reversed':
      return '';
    case 'align':
      return ALIGN_VALUES.has(lower) ? lower : null;
    case 'valign':
      return VALIGN_VALUES.has(lower) ? lower : null;
    case 'scope':
      return SCOPE_VALUES.has(lower) ? lower : null;
    case 'bgcolor':
      return COLOR_VALUE.test(trimmed) ? trimmed : null;
    case 'headers':
      return HEADERS_VALUE.test(trimmed) ? trimmed : null;
    case 'datetime':
      return DATETIME_VALUE.test(trimmed) ? trimmed : null;
    case 'style':
      return sanitizeStyle(value) || null;
    default:
      // alt, title, class, rel
      return value;
  }
}

function copyAttributes(src: Element, dest: Element): void {
  const tag = dest.localName;
  for (const attr of Array.from(src.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on') || !isAllowedAttr(tag, name)) continue;
    const value = attributeValue(name, attr.value);
    if (value !== null) dest.setAttribute(name, value);
  }
  if (tag === 'a' && dest.getAttribute('target') === '_blank') {
    dest.setAttribute('rel', 'noopener noreferrer');
  }
}

function cleanChildren(src: Node, dest: Node, doc: Document): void {
  for (const child of Array.from(src.childNodes)) {
    if (child.nodeType === 3) {
      dest.appendChild(doc.createTextNode(child.textContent || ''));
      continue;
    }
    if (child.nodeType !== 1) continue; // commenti, istruzioni: via
    const el = child as Element;
    const tag = el.localName.toLowerCase();
    if (DROP_TAGS.has(tag)) continue;
    // Elementi SVG/MathML o di altri namespace: mai ammessi.
    if (el.namespaceURI && el.namespaceURI !== HTML_NS) continue;
    if (ALLOWED_TAGS.has(tag)) {
      const out = doc.createElement(tag);
      copyAttributes(el, out);
      cleanChildren(el, out, doc);
      dest.appendChild(out);
    } else if (BLOCK_TO_DIV.has(tag)) {
      // Blocco non ammesso: un <div> senza attributi che tiene i figli ripuliti.
      const out = doc.createElement('div');
      cleanChildren(el, out, doc);
      dest.appendChild(out);
    } else {
      // Tag non consentito: resta solo il suo contenuto ripulito.
      cleanChildren(el, dest, doc);
    }
  }
}

export function sanitizeHtml(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') {
    // Nessun DOM disponibile: meglio nessun markup che markup non controllato.
    return html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const container = doc.createElement('div');
  cleanChildren(doc.body, container, doc);
  return container.innerHTML;
}

export default sanitizeHtml;
